import React, { createContext, useEffect, useState } from "react";
import siteConfig from "../config/siteConfig";
import productData from "../Datas/productData";

export const CartContext = createContext(null);

const readStoredValue = (key, fallback) => {
  if (typeof window === "undefined") return fallback;

  try {
    const value = window.localStorage.getItem(key);
    if (!value) return fallback;
    const parsedValue = JSON.parse(value);
    return Array.isArray(parsedValue) ? parsedValue : fallback;
  } catch {
    return fallback;
  }
};

const writeStoredValue = (key, value) => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage may be disabled; the in-memory shopping session still works.
  }
};

const validProductIds = new Set(productData.map((product) => product.id));

const normalizeQuantity = (quantity) => {
  const numericQuantity = Number(quantity);
  if (!Number.isFinite(numericQuantity)) return 1;
  return Math.min(99, Math.max(1, Math.floor(numericQuantity)));
};

const sanitizeCart = (items) =>
  items
    .filter((item) => item && typeof item === "object")
    .map((item) => ({
      id: Number(item.id),
      size: typeof item.size === "string" ? item.size : "",
      color: typeof item.color === "string" ? item.color : "",
      quantity: normalizeQuantity(item.quantity),
    }))
    .filter((item) => validProductIds.has(item.id));

const sanitizeWishlist = (items) => [
  ...new Set(
    items
      .map((id) => Number(id))
      .filter((id) => validProductIds.has(id))
  ),
];

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() =>
    sanitizeCart(readStoredValue(siteConfig.storage.cart, []))
  );
  const [wishlistItems, setWishlistItems] = useState(() =>
    sanitizeWishlist(readStoredValue(siteConfig.storage.wishlist, []))
  );

  useEffect(() => {
    writeStoredValue(siteConfig.storage.cart, cartItems);
  }, [cartItems]);

  useEffect(() => {
    writeStoredValue(siteConfig.storage.wishlist, wishlistItems);
  }, [wishlistItems]);

  const addToCart = (productId, quantity = 1, size = "", color = "") => {
    const safeQuantity = normalizeQuantity(quantity);

    setCartItems((currentItems) => {
      const existingItem = currentItems.find(
        (item) =>
          item.id === productId && item.size === size && item.color === color
      );

      if (!existingItem) {
        return [
          ...currentItems,
          { id: productId, size, color, quantity: safeQuantity },
        ];
      }

      return currentItems.map((item) =>
        item.id === productId && item.size === size && item.color === color
          ? {
              ...item,
              quantity: normalizeQuantity(item.quantity + safeQuantity),
            }
          : item
      );
    });
  };

  const removeFromCart = (productId, size = "", color = "") => {
    setCartItems((currentItems) =>
      currentItems.filter(
        (item) =>
          !(item.id === productId && item.size === size && item.color === color)
      )
    );
  };

  const updateQuantity = (productId, size, color, quantity) => {
    const safeQuantity = normalizeQuantity(quantity);
    setCartItems((currentItems) =>
      currentItems.map((item) =>
        item.id === productId && item.size === size && item.color === color
          ? { ...item, quantity: safeQuantity }
          : item
      )
    );
  };

  const clearCart = () => setCartItems([]);

  const addToWishlist = (productId) => {
    setWishlistItems((currentItems) =>
      currentItems.includes(productId)
        ? currentItems
        : [...currentItems, productId]
    );
  };

  const removeFromWishlist = (productId) => {
    setWishlistItems((currentItems) =>
      currentItems.filter((id) => id !== productId)
    );
  };

  const toggleWishlist = (productId) => {
    setWishlistItems((currentItems) =>
      currentItems.includes(productId)
        ? currentItems.filter((id) => id !== productId)
        : [...currentItems, productId]
    );
  };

  const value = {
    cartItems,
    wishlistItems,
    cartCount: cartItems.reduce((total, item) => total + item.quantity, 0),
    wishlistCount: wishlistItems.length,
    addToCart,
    addCart: addToCart,
    removeFromCart,
    removeCart: removeFromCart,
    updateQuantity,
    clearCart,
    addToWishlist,
    removeFromWishlist,
    toggleWishlist,
    isWishlisted: (productId) => wishlistItems.includes(productId),
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
